import { connectStreamSource, disconnectStreamSource, session } from "@hotwired/turbo"
import { createConsumer } from "@rails/actioncable"

// Disable Turbo Drive — we only use Turbo Streams on the omni page.
session.drive = false

// Lazy-initialized ActionCable consumer
let consumer

function getConsumer() {
  if (!consumer) consumer = createConsumer()
  return consumer
}

// Minimal replica of turbo-rails' <turbo-cable-stream-source> custom element.
// Subscribes to a Turbo::StreamsChannel and feeds incoming messages into Turbo Streams.
class TurboCableStreamSourceElement extends HTMLElement {
  connectedCallback() {
    connectStreamSource(this)
    const channel = this.getAttribute("channel")
    const signed_stream_name = this.getAttribute("signed-stream-name")

    this.subscription = getConsumer().subscriptions.create(
      { channel, signed_stream_name },
      {
        received: (data) => {
          this.dispatchEvent(new MessageEvent("message", { data }))
        }
      }
    )
  }

  disconnectedCallback() {
    disconnectStreamSource(this)
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }
}

if (customElements.get("turbo-cable-stream-source") === undefined) {
  customElements.define("turbo-cable-stream-source", TurboCableStreamSourceElement)
}

// Thinking block: auto-scroll, fade overlay, and post-search collapse.
// Returns a cleanup function that disconnects all observers.
function initThinking(thinkingEl, wrapper) {
  let pinned = true
  let searchComplete = false
  const observers = []
  wrapper.style.display = null

  const SCROLL_TOLERANCE = 80

  function isAtBottom() {
    return thinkingEl.scrollHeight - thinkingEl.scrollTop - thinkingEl.clientHeight <= SCROLL_TOLERANCE
  }

  function hasOverflow() {
    return thinkingEl.scrollHeight > thinkingEl.clientHeight
  }

  function updateClasses() {
    if (searchComplete) return
    wrapper.classList.toggle("has-overflow", hasOverflow() && !isAtBottom())
    wrapper.classList.toggle("pinned-bottom", pinned)
  }

  // Only user-initiated scrolls unpin. Programmatic scrollTop bumps in the mutation
  // handler also fire scroll events, but they happen outside the userInputWindow so
  // we know to ignore them. This avoids the timing races a single "updatingContent"
  // flag had against content-growth scroll events.
  let userInputExpected = false
  let userInputTimer = null

  function markUserInput() {
    userInputExpected = true
    clearTimeout(userInputTimer)
    userInputTimer = setTimeout(() => { userInputExpected = false }, 300)
  }

  thinkingEl.addEventListener("wheel", markUserInput, { passive: true })
  thinkingEl.addEventListener("touchstart", markUserInput, { passive: true })
  thinkingEl.addEventListener("touchmove", markUserInput, { passive: true })
  thinkingEl.addEventListener("mousedown", markUserInput)
  thinkingEl.addEventListener("keydown", markUserInput)

  thinkingEl.addEventListener("scroll", () => {
    if (userInputExpected) {
      pinned = isAtBottom()
    }
    updateClasses()
  })

  // Auto-scroll: rAF loop instead of MutationObserver. Turbo Stream's chunk-inner updates
  // (broadcast_update_to on a chunk div) don't reliably surface as observable mutations on
  // the parent before scrollHeight has settled, and the timing was racing the layout pass.
  // A frame-paced loop just pins unconditionally while active — robust against any of that.
  let scrollLoopActive = true
  function scrollLoop() {
    if (!scrollLoopActive) return
    if (pinned) thinkingEl.scrollTop = thinkingEl.scrollHeight
    updateClasses()
    requestAnimationFrame(scrollLoop)
  }
  requestAnimationFrame(scrollLoop)
  observers.push({ disconnect: () => { scrollLoopActive = false } })

  // Collapse thinking once response generation begins
  const responseContainer = document.getElementById("omni-response-container")
  if (responseContainer) {
    const respObs = new MutationObserver((mutations, obs) => {
      if (responseContainer.textContent.trim()) {
        obs.disconnect()
        collapseThinking()
      }
    })
    respObs.observe(responseContainer, { childList: true, subtree: true, characterData: true })
    observers.push(respObs)
  }

  const statusContainer = document.getElementById("omni-status-message")
  if (statusContainer) {
    const statusObs = new MutationObserver((mutations, obs) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-status') {
          const status = statusContainer.dataset.status
          if (status === 'generating') {
            obs.disconnect()
            collapseThinking()
          }
        }
      }
    })
    statusObs.observe(statusContainer, { attributes: true })
    observers.push(statusObs)
  }

  // Handle search completion: #omni-header gets removed by broadcast_complete.
  if (wrapper.parentElement) {
    const headerObs = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.removedNodes) {
          if (node.id === "omni-header") {
            searchComplete = true
            scrollLoopActive = false
            if (wrapper.dataset.userExpanded !== "true" && !wrapper.classList.contains("collapsed")) {
              collapseThinking()
            }
            return
          }
        }
      }
    })
    headerObs.observe(wrapper.parentElement, { childList: true })
    observers.push(headerObs)
  }

  function collapseThinking() {
    if (!thinkingEl.textContent.trim()) {
      wrapper.style.display = "none"
      return
    }

    if (wrapper.classList.contains("collapsed")) return

    wrapper.classList.remove("has-overflow", "pinned-bottom")
    wrapper.classList.add("collapsed")
    syncToggleLabel(wrapper)
  }

  // Return cleanup function
  return () => { observers.forEach(obs => obs.disconnect()) }
}

// Update the toggle button's chevron + label to match the current collapsed state.
function syncToggleLabel(wrapper) {
  const btn = wrapper.querySelector(".omni-thinking-toggle-btn")
  if (!btn) return
  const isCollapsed = wrapper.classList.contains("collapsed")
  const chevron = btn.querySelector(".omni-thinking-chevron")
  if (chevron) {
    chevron.classList.toggle("fa-chevron-right", isCollapsed)
    chevron.classList.toggle("fa-chevron-down", !isCollapsed)
  }
  if (btn.lastChild && btn.lastChild.nodeType === Node.TEXT_NODE) {
    btn.lastChild.textContent = isCollapsed ? " Show thinking" : " Hide thinking"
  }
}

// Delegated click handler — survives frame re-renders and chunk DOM mutations.
document.addEventListener("click", (e) => {
  const btn = e.target.closest(".omni-thinking-toggle-btn")
  if (!btn) return
  e.preventDefault()
  const wrapper = btn.closest("#omni-thinking-wrapper")
  if (!wrapper) return
  const isCollapsed = wrapper.classList.toggle("collapsed")
  syncToggleLabel(wrapper)
  if (!isCollapsed) {
    wrapper.dataset.userExpanded = "true"
    const thinkingEl = wrapper.querySelector("#omni-thinking")
    if (thinkingEl) thinkingEl.scrollTop = 0
  }
})

// Re-initialize thinking whenever the wrapper appears. turbo:frame-render fires before
// the new content is committed in some Turbo paths, so it's unreliable here. A MutationObserver
// scoped to #omni-results catches the wrapper whenever it appears, regardless of which Turbo
// event inserted it.
let cleanupThinking = null
let lastWrapper = null

function tryInitThinking() {
  const thinkingEl = document.getElementById("omni-thinking")
  const wrapper = document.getElementById("omni-thinking-wrapper")
  if (thinkingEl && wrapper && wrapper.parentElement) {
    if (cleanupThinking) cleanupThinking()
    cleanupThinking = initThinking(thinkingEl, wrapper)
  }
}

function watchForThinkingWrapper() {
  const frame = document.getElementById("omni-results")
  if (!frame) return
  new MutationObserver(() => {
    const wrapper = document.getElementById("omni-thinking-wrapper")
    if (wrapper && wrapper !== lastWrapper) {
      lastWrapper = wrapper
      tryInitThinking()
    }
  }).observe(frame, { childList: true, subtree: true })
}

if (document.body) {
  watchForThinkingWrapper()
} else {
  document.addEventListener("DOMContentLoaded", watchForThinkingWrapper, { once: true })
}

// Update the browser URL when a search is submitted so the query is shareable/bookmarkable.
document.addEventListener("turbo:before-fetch-request", (e) => {
  const frame = e.target.closest("turbo-frame")
  if (frame && frame.id === "omni-results") {
    const url = new URL(e.detail.url)
    // Remove stream_id from the visible URL — it's ephemeral
    url.searchParams.delete("stream_id")
    history.pushState(null, "", url.pathname + url.search)
  }
})

// Also try on initial page load
tryInitThinking()
